<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { Button, Input, PageHeader, Section, Spinner, Table, Toolbar } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { getContentAnalytics } from '../api/analytics.js'

const { t } = useI18n()
const filters = reactive({ since: day(-29), until: day(0) })
const report = ref(null)
const loading = ref(false)
const error = ref('')
const columns = computed(() => [
  { key: 'series', label: t('visualGameAdmin.contentAnalytics.series'), width: '180px' },
  { key: 'scenario_title', label: t('visualGameAdmin.contentAnalytics.scenario'), width: '220px' },
  { key: 'episode_no', label: t('visualGameAdmin.contentAnalytics.episode'), width: '100px', align: 'right', mono: true },
  { key: 'active_players', label: t('visualGameAdmin.contentAnalytics.activePlayers'), width: '130px', align: 'right', mono: true },
  { key: 'runs_started', label: t('visualGameAdmin.contentAnalytics.started'), width: '110px', align: 'right', mono: true },
  { key: 'runs_completed', label: t('visualGameAdmin.contentAnalytics.completed'), width: '110px', align: 'right', mono: true },
  { key: 'completion_rate', label: t('visualGameAdmin.contentAnalytics.completionRate'), width: '120px', align: 'right', mono: true },
  { key: 'interactions', label: t('visualGameAdmin.contentAnalytics.interactions'), width: '120px', align: 'right', mono: true },
  { key: 'say_turns', label: t('visualGameAdmin.contentAnalytics.dialogues'), width: '110px', align: 'right', mono: true },
  { key: 'act_turns', label: t('visualGameAdmin.contentAnalytics.actions'), width: '110px', align: 'right', mono: true },
])

onMounted(load)

async function load() {
  loading.value = true
  error.value = ''
  try {
    report.value = await getContentAnalytics({ since: iso(filters.since), until: isoNext(filters.until) })
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    loading.value = false
  }
}

function day(offset) {
  const value = new Date()
  value.setHours(0, 0, 0, 0)
  value.setDate(value.getDate() + offset)
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`
}
function iso(value) {
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) throw new Error(`Invalid analytics date: ${value}`)
  return date.toISOString()
}
function isoNext(value) {
  const date = new Date(iso(value))
  date.setDate(date.getDate() + 1)
  return date.toISOString()
}
function seriesLabel(row) { return row.series_title === null ? t('visualGameAdmin.contentAnalytics.standalone') : row.series_title }
function rate(value) { return value === null ? '—' : `${(value * 100).toFixed(1)}%` }
</script>

<template>
  <div class="vga-page">
    <PageHeader :title="t('visualGameAdmin.contentAnalytics.title')" />
    <Section><Toolbar>
      <div class="drama-toolbar-field"><label>{{ t('visualGameAdmin.analytics.since') }}</label><Input v-model="filters.since" type="date" size="sm" /></div>
      <div class="drama-toolbar-field"><label>{{ t('visualGameAdmin.analytics.until') }}</label><Input v-model="filters.until" type="date" size="sm" /></div>
      <template #actions><Button :loading="loading" @click="load">{{ t('visualGameAdmin.analytics.refresh') }}</Button></template>
    </Toolbar></Section>
    <div v-if="loading && report === null" class="vga-state"><Spinner /></div>
    <div v-else-if="error" class="vga-state"><p class="vga-error">{{ error }}</p></div>
    <Section v-else-if="report !== null" :padded="false">
      <Table :columns="columns" :rows="report.items" row-key="scenario_id">
        <template #series="{ row }">{{ seriesLabel(row) }}</template>
        <template #completion_rate="{ row }">{{ rate(row.completion_rate) }}</template>
      </Table>
    </Section>
  </div>
</template>
